import type { MapDefinition } from '../game/model/types'
import { validateMap } from '../game/model/validateMap'
import { normalizeMapRoutes } from '../game/model/path'

export const MAP_SCHEMA_VERSION = 1
export const MAX_IMPORT_BYTES = 1_500_000

export type ImportMapResult =
  | { ok: true; map: MapDefinition }
  | { ok: false; errors: string[] }

/** Stable pretty JSON of gameplay MapDefinition only (no editor state). */
export function serializeMapDefinition(map: MapDefinition): string {
  const canonical: MapDefinition = {
    schemaVersion: map.schemaVersion ?? MAP_SCHEMA_VERSION,
    id: map.id,
    name: map.name,
    width: map.width,
    height: map.height,
    rows: [...map.rows],
    spawn: { ...map.spawn },
    exit: { ...exitClone(map.exit) },
    path: map.path.map((c) => ({ x: c.x, y: c.y })),
    startingGold: map.startingGold,
    startingLives: map.startingLives,
    waves: map.waves.map((wave) => ({
      id: wave.id,
      completionReward: wave.completionReward,
      groups: wave.groups.map((group) => {
        const base = {
          enemy: group.enemy,
          count: group.count,
          interval: group.interval,
          delay: group.delay
        }
        return {
          ...base,
          ...(group.enemyLevel !== undefined ? { enemyLevel: group.enemyLevel } : {}),
          ...(group.enemyRank !== undefined ? { enemyRank: group.enemyRank } : {}),
          ...(group.routeId !== undefined ? { routeId: group.routeId } : {})
        }
      })
    }))
  }
  // schemaVersion 1 stays dual-written: canonical routes[] plus the legacy
  // path/airRoute fields older readers rely on.
  if (map.airRoute) {
    canonical.airRoute = {
      id: map.airRoute.id || 'air',
      spawn: { ...map.airRoute.spawn },
      exit: { ...map.airRoute.exit },
      cells: map.airRoute.cells.map((c) => ({ x: c.x, y: c.y }))
    }
  }
  const routes = normalizeMapRoutes(map)
  if (routes.length > 0) {
    canonical.routes = routes.map((route) => ({
      id: route.id,
      movementClass: route.movementClass,
      spawn: { ...route.spawn },
      exit: { ...route.exit },
      cells: route.cells.map((c) => ({ x: c.x, y: c.y }))
    }))
  }
  return `${JSON.stringify(canonical, null, 2)}\n`
}

function exitClone(exit: { x: number; y: number }): { x: number; y: number } {
  return { x: exit.x, y: exit.y }
}

export function parseImportedMapJson(raw: string): ImportMapResult {
  if (raw.length > MAX_IMPORT_BYTES) {
    return { ok: false, errors: [`JSON exceeds ${MAX_IMPORT_BYTES} bytes`] }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { ok: false, errors: ['Malformed JSON — could not parse file'] }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, errors: ['Root value must be a map object'] }
  }

  const candidate = parsed as Partial<MapDefinition>
  const version = candidate.schemaVersion ?? MAP_SCHEMA_VERSION
  if (typeof version !== 'number' || !Number.isInteger(version)) {
    return { ok: false, errors: ['schemaVersion must be an integer'] }
  }
  if (version !== MAP_SCHEMA_VERSION) {
    return {
      ok: false,
      errors: [`Unsupported schemaVersion ${version} (expected ${MAP_SCHEMA_VERSION})`]
    }
  }

  const map = {
    ...candidate,
    schemaVersion: version
  } as MapDefinition

  const validated = validateMap(map)
  if (!validated.ok) {
    return {
      ok: false,
      errors: validated.errors.map((e) => e.message)
    }
  }

  // Accept legacy (path/airRoute only) or routes[] authoring; hand back both.
  const loaded = structuredClone(map)
  const routes = normalizeMapRoutes(loaded)
  if (routes.length > 0) loaded.routes = routes
  return { ok: true, map: loaded }
}

export function downloadMapJson(map: MapDefinition): void {
  const text = serializeMapDefinition(map)
  const blob = new Blob([text], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${map.id || 'map'}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}
